import { isFocusable } from './focus';

// The modal dialogs open on the page, oldest first. A modal <dialog> makes
// everything outside it inert, so what has to stay usable above one (toasts)
// moves into the newest, and only the newest answers Escape.
const openModals: HTMLDialogElement[] = [];
const suspendedModals = new WeakSet<HTMLDialogElement>();
const listeners = new Set<() => void>();

export function pushModal(dialog: HTMLDialogElement) {
   const index = openModals.indexOf(dialog);
   if (index !== -1) openModals.splice(index, 1);
   openModals.push(dialog);
   notify();
}

export function removeModal(dialog: HTMLDialogElement) {
   const index = openModals.indexOf(dialog);
   if (index === -1) return;
   openModals.splice(index, 1);
   notify();
}

export function topModal(): HTMLDialogElement | undefined {
   return openModals.at(-1);
}

/**
 * Makes the open modal dialogs plain dialogs, still on screen, until the
 * returned function runs. For a window a third party adds at the end of
 * <body>, which a modal leaves under it and inert: Google's reCAPTCHA
 * challenge.
 */
export function suspendModals(): () => void {
   const suspended = openModals.filter(dialog => dialog.open);
   if (suspended.length === 0) return () => {};
   const focused = document.activeElement;
   // Where each dialog returns focus once it closes for good. Closing a modal
   // one hands focus back there, but Chrome forgets it for a plain dialog, so
   // it is noted here and restored before showModal() notes it again.
   const returnFocusTo = new Map<HTMLDialogElement, Element | null>();
   // Newest first, so each hands focus back into the one under it while that
   // one is still modal.
   for (const dialog of suspended.slice().reverse()) {
      // The close event this queues finds the dialog open again, and its
      // listener ignores it.
      dialog.close();
      returnFocusTo.set(dialog, document.activeElement);
      dialog.show();
      suspendedModals.add(dialog);
   }
   // show() moved focus onto a dialog; put it back where it was.
   if (focused?.isConnected && isFocusable(focused)) focused.focus({ preventScroll: true });
   return () => {
      for (const dialog of suspended) suspendedModals.delete(dialog);
      // Oldest first, so they stack again as they did.
      for (const dialog of openModals) {
         if (!suspended.includes(dialog) || !dialog.open) continue;
         dialog.close();
         const returnTo = returnFocusTo.get(dialog);
         if (returnTo && isFocusable(returnTo)) returnTo.focus({ preventScroll: true });
         dialog.showModal();
      }
      if (focused && isFocusable(focused) && topModal()?.contains(focused)) {
         focused.focus({ preventScroll: true });
      }
      notify();
   };
}

/** A suspended dialog doesn't keep Tab inside it, so it can reach the challenge. */
export function isSuspended(dialog: HTMLDialogElement) {
   return suspendedModals.has(dialog);
}

/** Runs `listener` whenever a modal dialog opens, closes or is shown again. */
export function subscribeToModals(listener: () => void): () => void {
   listeners.add(listener);
   return () => {
      listeners.delete(listener);
   };
}

function notify() {
   for (const listener of listeners) listener();
}
